import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  construirRutaDeAlta,
  leerContextoDeRegreso,
  publicarContextoDeListado,
  retirarContextoDeListado,
} from './contextoDeNavegacion.js';

/*
 * LOS DOS GANCHOS DEL CONTEXTO DE NAVEGACIÓN (RF-145, RF-146, RF-148, D-237).
 *
 * Herencia de ida y vuelta al mismo sitio son UNA SOLA REGLA, no dos: separarlas
 * produce justo el defecto de hoy —el botón de alta manda su contexto y el
 * regreso aterriza en un destino fijo— (CR-01, D-237).
 */

/**
 * Lo usa toda PANTALLA DE ALTA o de EDICIÓN. Devuelve:
 *   · `destinoDeRegreso`: la dirección exacta de la que se vino, o el destino
 *     padre de siempre si no se vino de ningún contexto (RN-390c).
 *   · `regresar()`: va ahí REEMPLAZANDO el paso del formulario en el historial
 *     (RF-148, RN-396). Se reemplaza SIEMPRE el paso del formulario y NUNCA el
 *     del origen: reemplazar de más deja la flecha de volver sin paso al que
 *     volver y saca a la persona de la aplicación con el botón atrás.
 */
export function useContextoDeRegreso(destinoPadre) {
  const ubicacion = useLocation();
  const navegar = useNavigate();

  const destinoDeRegreso = useMemo(
    () => leerContextoDeRegreso(ubicacion.search, destinoPadre),
    [ubicacion.search, destinoPadre],
  );

  const regresar = useCallback(
    (destino = destinoDeRegreso) => navegar(destino, { replace: true }),
    [navegar, destinoDeRegreso],
  );

  return { destinoDeRegreso, regresar };
}

/**
 * Lo usa toda PANTALLA DE LISTADO con contexto. Publica su dirección exacta
 * mientras está a la vista, para que el botón flotante del armazón arme las
 * direcciones de las altas heredando ese contexto (RF-150, RN-411) sin que el
 * armazón tenga que saber qué rutas son listados.
 *
 * `sugerencias` es lo que el filtro determina SIN AMBIGÜEDAD (RN-387, RN-388). La
 * cuenta no se sugiere nunca (RF-153).
 */
export function useContextoDeListado(sugerencias = null) {
  const ubicacion = useLocation();
  const direccion = `${ubicacion.pathname}${ubicacion.search}`;

  // En el render, no en un efecto: ver la nota de contextoDeNavegacion.js.
  publicarContextoDeListado({ direccion, sugerencias: sugerencias ?? null });

  useEffect(() => () => retirarContextoDeListado(direccion), [direccion]);

  return direccion;
}

/** Arma la dirección de un alta heredando el contexto de la pantalla de origen. */
export function rutaDeAltaDesde(ruta, direccionDeOrigen, sugerencias = null) {
  return construirRutaDeAlta(ruta, { contextoDeRegreso: direccionDeOrigen, sugerencias });
}
